<template>
  <nav
    ref="navigationRef"
    class="product-nav"
    :class="{ sticky: isShownProduct() }"
  >
    <ul class="product-nav__list">
      <template v-for="(tab, index) in tabs" :key="index">
        <li
          v-if="undefined === tab.isShow || tab.isShow()"
          class="product-nav__item-w"
        >
          <ProductLink
            :name="tab.routeName"
            :product="product"
            class="product-nav__item"
            :class="{ active: isRouteMatched(tab.routeName) }"
          >
            {{ _T(tab.title) }}
          </ProductLink>
        </li>
      </template>
    </ul>
    <div v-if="isShownProduct()" class="product-card-nav">
      <img
        class="product-card-nav__picture"
        :src="product.getMainImage('small')"
        :alt="cleanStringsConcat([product.getName(), 'image'])"
      />
      <p class="product-card-nav__title">
        {{ product.getName() }}
      </p>
      <ProductPrice :product="product" :is-comparison="true" />
      <ButtonBuy :product="product" :minified-view="true" />
    </div>
  </nav>
</template>

<script setup>
import { useHeaderStore } from "~/store/headerStore";
import ButtonBuy from "~/modules/shared/buttons/ButtonBuy.vue";
import { Product } from "~/models/product.model";
import ProductPrice from "~/modules/shared/price/ProductPrice.vue";
import { addScopedEventListener } from "~/utils/eventListener";

const props = defineProps({
  tabs: { type: Array, required: true },
  product: { type: Product, required: true },
});

const headerStore = useHeaderStore();
const { getHeightHeader } = storeToRefs(headerStore);

const navigationRef = ref(null);
const isMobile = ref(false);

const { top } = useElementBounding(navigationRef);
const { width: windowSize } = useWindowSize();

onMounted(() => {
  const debounced = useDebounceFn(() => {
    getMobile();
  }, 50);

  nextTick(() => {
    getMobile();
  });

  addScopedEventListener(window, "resize", debounced);
});

const getMobile = () => {
  isMobile.value = windowSize.value <= 1024;
};

const headerHeight = () => {
  return getHeightHeader.value.header;
};

const isShownProduct = () => {
  return top.value + "px" === headerHeight() && !isMobile.value;
};
</script>

<style lang="scss" scoped>
.product-nav {
  @extend %width-main;

  position: sticky;
  top: v-bind("headerHeight()");
  z-index: 100;

  @include flex-container();
  gap: 16px;

  background-color: white;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;

  overflow: auto hidden;

  &.sticky {
    box-shadow: 0 1px 0 rgb(0, 0, 0, 0.2);

    & .product-nav__list {
      overflow: auto hidden;
      border-bottom: none;
    }

    & .product-nav__item {
      &:after {
        bottom: 0;
      }
    }
  }

  @include mobile {
    border-radius: 0;
  }

  &__list {
    width: 100%;
    @include flex-container(row, space-evenly, center);
    gap: 16px;

    border-bottom: 2px solid var(--color-sky-base);

    @include mobile {
      justify-content: flex-start;
      gap: 0;
    }
  }

  &__item-w {
    display: flex;
  }

  &__item {
    cursor: pointer;
    position: relative;

    text-align: center;
    @include font(18, 24);
    white-space: nowrap;

    padding: 16px 16px 8px;

    transition: color 0.2s;

    &:hover {
      color: var(--color-primary-dark);
    }

    &.active {
      color: var(--color-primary-base);

      &::after {
        content: "";
        position: absolute;
        bottom: -2px;
        left: 0;

        width: 100%;
        border-bottom: 2px solid var(--color-primary-base);
      }

      &:hover {
        color: var(--color-primary-dark);
      }
    }

    @include bigMobile {
      @include font(16, 22);

      padding: 8px 16px;
    }

    @include mobile {
      @include font(14, 20, 500);
      white-space: nowrap;
    }
  }
}

.product-card-nav {
  max-width: 380px;
  width: 100%;

  @include flex-container(row, flex-start, center);
  gap: 8px;

  &__picture {
    @include fixedHW(48px);
  }

  &__title {
    display: -webkit-box;

    max-width: 160px;
    width: 100%;

    @include font(12, 16);
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  &__price {
    @include font(18, 22, 600);
  }
}
</style>
